import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Search, ChevronDown, LogOut, LogIn, UserCircle, Briefcase, Bell, Tag, Plus, Library, HeartHandshake, ShieldCheck, Settings, Heart } from 'lucide-react';
import { InboxLink } from './MessagingContext';
import { Bookmark, MessageSquare } from 'lucide-react';
import { Avatar } from './Avatar';
import { useI18n } from '../i18n';
import { useAuth } from '../auth/AuthContext';
import StageCrewLogo from './StageCrewLogo';
import { useStageMotion } from './StageMotion';

const NAV_ITEMS = [
  { key: 'nav.ofertas', to: '/ofertas' },
  { key: 'nav.profesionales', to: '/profesionales' },
  { key: 'nav.empresas', to: '/empresas' },
  { key: 'content.hubTitle', to: '/actualidad' },
  { key: 'nav.forum', to: '/foro' },
  { key: 'nav.mercado', to: '/mercado' },
  { key: 'nav.biblioteca', to: '/biblioteca' },
  { key: 'nav.colaboracion', to: '/colabora' },
  { key: 'nav.donaciones', to: '/donaciones' },
];

const LocaleSwitcher = () => {
  const { locale, setLocale, available } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="locale-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-amber-500 transition-colors px-2 py-1"
      >
        {locale}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-24 bg-[#121418] border border-[#2A2F3D] rounded-lg overflow-hidden z-50">
          {available.map((l) => (
            <button
              key={l}
              data-testid={`locale-option-${l}`}
              onClick={() => { setLocale(l); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs font-mono uppercase tracking-wider hover:bg-amber-500/10 hover:text-amber-500 transition-colors ${locale === l ? 'text-amber-500' : 'text-slate-300'}`}
            >
              {l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const { playIntro } = useStageMotion();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <header
        data-testid="main-navbar"
        className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0C0E]/85 border-b border-[#2A2F3D]"
      >
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <StageCrewLogo size="md" onClick={playIntro} />


          <nav className="hidden 2xl:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                data-testid={`nav-link-${item.to.replace('/', '')}`}
                className={({ isActive }) =>
                  `px-2 py-2 text-sm font-medium whitespace-nowrap transition-colors rounded-md ${
                    isActive ? 'text-amber-500' : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden 2xl:flex items-center gap-3 shrink-0">
            <Link
              to="/publicar-oferta"
              data-testid="publish-job-btn"
              className="hidden xl:inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-amber-500 border border-amber-500/40 hover:bg-amber-500/10 px-3 py-2 rounded-md transition-colors"
            >
              + {t('nav.publish')}
            </Link>
            <button
              data-testid="global-search-btn"
              onClick={() => navigate('/ofertas')}
              className="w-9 h-9 flex items-center justify-center rounded-md text-slate-400 hover:text-amber-500 hover:bg-white/5 transition-colors"
              aria-label={t('nav.search')}
            >
              <Search size={16} />
            </button>
            <LocaleSwitcher />
            {user ? (
              <div className="relative">
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md border border-[#2A2F3D] hover:border-amber-500/40 transition-colors"
                >
                  <Avatar src={user.avatar_url || user.picture} name={user.nick || user.name} size={28} />
                  <span className="text-sm text-slate-300 max-w-[120px] truncate">{user.nick || user.name}</span>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-[#121418] border border-[#2A2F3D] rounded-lg overflow-hidden z-50 p-2">
                    <div className="px-3 py-2 text-xs text-slate-500 font-mono uppercase tracking-wider border-b border-[#2A2F3D] mb-1 truncate">{user.handle ? `@${user.handle}` : user.email}</div>
                    {[
                      ['/mi-perfil', 'my-profile-link', UserCircle, t('profile.myLink')],
                      ['/mi-perfil?tab=configuracion', 'my-configuration-link', Settings, 'Configuración'],
                      ['/mi-perfil?tab=candidaturas', 'menu-applications', Briefcase, 'Mis candidaturas'],
                      ['/mensajes', 'menu-messages', MessageSquare, t('chat.title')],
                      ['/contactos', 'menu-saved-contacts', Bookmark, t('chat.saved')],
                      ['/mi-perfil?tab=avisos', 'menu-notifications', Bell, 'Avisos'],
                      ['/publicar-oferta', 'menu-publish', Plus, 'Publicar oferta'],
                      ['/mercado/publicar', 'menu-sell', Tag, 'Publicar anuncio'],
                      ['/biblioteca', 'menu-library', Library, 'Biblioteca'],
                      ['/colabora', 'menu-collaborate', HeartHandshake, 'Colabora'],
                      ['/donaciones', 'menu-support', Heart, 'Donaciones'],
                    ].map(([to, tid, Icon, label]) => (
                      <Link key={tid} to={to} data-testid={tid} onClick={() => setUserMenuOpen(false)} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-amber-500 hover:bg-amber-500/5 rounded-md">
                        <Icon size={14} /> {label}
                      </Link>
                    ))}
                    {(user.role === 'admin' || user.role === 'moderator') && (
                      <Link to="/admin" data-testid="menu-admin" onClick={() => setUserMenuOpen(false)} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-sky-300 hover:bg-sky-500/10 rounded-md border-t border-[#2A2F3D] mt-1 pt-2.5">
                        <ShieldCheck size={14} /> {user.role === 'admin' ? 'Administración' : 'Moderación'}
                      </Link>
                    )}
                    <button
                      data-testid="logout-btn"
                      onClick={() => { setUserMenuOpen(false); logout(); }}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-red-300 hover:bg-red-500/10 rounded-md border-t border-[#2A2F3D] mt-1 pt-2.5"
                    >
                      <LogOut size={14} /> Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="login-link" className="text-sm text-slate-300 hover:text-white transition-colors px-3 py-2">
                  {t('nav.login')}
                </Link>
                <Link to="/registro" data-testid="register-link" className="text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-slate-950 px-4 py-2 rounded-md transition-colors">
                  {t('nav.register')}
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
          <InboxLink />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="2xl:hidden w-9 h-9 flex items-center justify-center rounded-md text-slate-300"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="2xl:hidden max-h-[calc(100dvh_-_4rem)] overflow-y-auto overscroll-contain border-t border-[#2A2F3D] bg-[#0B0C0E]" data-testid="mobile-menu-panel">
            <nav className="px-4 py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`mobile-nav-${item.to.replace('/', '')}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base rounded-md ${isActive ? 'text-amber-500 bg-amber-500/5' : 'text-slate-200'}`
                  }
                >
                  {t(item.key)}
                </NavLink>
              ))}
              <div className="mt-3 pt-3 border-t border-[#2A2F3D] flex flex-col gap-1">
                <Link to="/contacto" onClick={() => setMobileOpen(false)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-300 hover:text-amber-500 hover:bg-amber-500/5 rounded-md" data-testid="mobile-contact">
                  Contacto
                </Link>
                {user ? (
                  <>
                    <div className="px-3 py-2 text-xs font-mono uppercase tracking-wider text-slate-500 truncate" data-testid="mobile-user-handle">{user.handle ? `@${user.handle}` : user.email}</div>
                    {[
                      ['/mi-perfil', 'mobile-my-profile', UserCircle, t('profile.myLink')],
                      ['/mi-perfil?tab=configuracion', 'mobile-configuration', Settings, 'Configuración'],
                      ['/mi-perfil?tab=candidaturas', 'mobile-applications', Briefcase, 'Mis candidaturas'],
                      ['/mensajes', 'mobile-messages', MessageSquare, t('chat.title')],
                      ['/contactos', 'mobile-saved-contacts', Bookmark, t('chat.saved')],
                      ['/mi-perfil?tab=avisos', 'mobile-notifications', Bell, 'Avisos'],
                      ['/publicar-oferta', 'mobile-publish', Plus, 'Publicar oferta'],
                      ['/mercado/publicar', 'mobile-sell', Tag, 'Publicar anuncio'],
                      ['/biblioteca', 'mobile-library', Library, 'Biblioteca'],
                      ['/colabora', 'mobile-collaborate', HeartHandshake, 'Colabora'],
                    ].map(([to, tid, Icon, label]) => (
                      <Link key={tid} to={to} data-testid={tid} onClick={() => setMobileOpen(false)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-300 hover:text-amber-500 hover:bg-amber-500/5 rounded-md">
                        <Icon size={15} /> {label}
                      </Link>
                    ))}
                    {(user.role === 'admin' || user.role === 'moderator') && (
                      <Link to="/admin" data-testid="mobile-admin" onClick={() => setMobileOpen(false)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-sky-300 hover:bg-sky-500/10 rounded-md">
                        <ShieldCheck size={15} /> {user.role === 'admin' ? 'Administración' : 'Moderación'}
                      </Link>
                    )}
                    <div className="my-1 border-t border-[#2A2F3D]" />
                    <button data-testid="mobile-logout" onClick={() => { setMobileOpen(false); logout(); }} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-red-300 hover:bg-red-500/10 rounded-md">
                      <LogOut size={15} /> Cerrar sesión
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-300 hover:text-amber-500 hover:bg-amber-500/5 rounded-md" data-testid="mobile-login">
                      <LogIn size={15} /> {t('nav.login')}
                    </Link>
                    <Link to="/registro" onClick={() => setMobileOpen(false)} className="px-3 py-2.5 mt-1 bg-amber-500 text-slate-950 font-semibold rounded-md text-center text-sm" data-testid="mobile-register">
                      {t('nav.register')}
                    </Link>
                  </>
                )}
                <div className="pt-2"><LocaleSwitcher /></div>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Navbar;
